import React, { Fragment, useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { Menu } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { getVendorName } from "../../../utils/getVendorName";
import { isSchemeMembershipEnabled } from "../../../utils/schemeMembershipFlag";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { HiOutlinePlus } from "react-icons/hi";
import { hasPermission } from "../../../utils/hasPermission";
import silver from "../../../assests/tenant/images/Silver.svg";
import Gold from "../../../assests/tenant/images/Gold.svg";
import platinum from "../../../assests/tenant/images/Platinum.svg";
import { toast } from "react-hot-toast";
import {
  useDeleteMembershipByIdMutation,
  useFetchAllMembershipMutation,
  useFetchMembershipByIdMutation,
} from "../../../features/tenant/membership/membershipApi";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import { BsCheckCircleFill, BsCircle } from "react-icons/bs";
import { HiEye } from "react-icons/hi2";

// Pagination handlers in this screen are commented out, so the list was
// silently capped at 10 memberships. Until real paging is wired, fetch a
// page big enough to show every config a tenant realistically has.
const LIST_PAGE_SIZE = 100;


function convertToTierFormat(dataArray, type = 1) {
  const result = {};

  dataArray.sort((a, b) => b.tier.rank - a.tier.rank);

  if (type == 0) {
    dataArray.forEach((entry) => {
      result[entry.tier.name] = {
        id: entry.id,
        rangeType: entry.range_type,
        rangeStart: entry.range_start,
        rangeEnd: entry.range_end,
        perMonth: entry.per_month || 0,
        pointsMultiplier: parseFloat(entry.points),
        giftDiscount: parseFloat(entry.gift_discount),
        cashback: entry.cashback,
        cash_benefit: entry.cash_benefit,
        bonusType: entry.tier.bonus_type || "fixed",
        bonus: parseFloat(entry.tier.bonus || 0),
      };
    });
  } else {
    dataArray.forEach((entry) => {
      result[entry.tier.name] = {
        rangeType:
          entry.range_type == 1
            ? "Purchase"
            : entry.range_type == 2
            ? "Scan"
            : entry.range_type == 3
            ? "QrMrp"
            : "Points",
        rangeStart: entry.range_start,
        rangeEnd: entry.range_end,
        perMonth: entry.per_month || 0,
        pointsMultiplier: parseFloat(entry.points),
        giftDiscount: parseFloat(entry.gift_discount),
        cashback: entry.cashback,
        cash_benefit: entry.cash_benefit,
        bonusType: entry.tier.bonus_type || "fixed",
        bonus: parseFloat(entry.tier.bonus || 0),
      };
    });
  }

  return result;
}

/**
 * Scheme membership slab view.
 *
 * Ordered by range_start ASCENDING — lowest band first. range_start is what the
 * engine actually orders by (DefaultStrategy.resolveTier sorts on it and ignores
 * tier.rank entirely, because clients use both rank conventions). Rendering by
 * rank would therefore show an order the payout engine does not agree with.
 */
const SchemeSlabTable = ({ slabs, meta }) => {
  const isPercent = meta?.reward_type === "percentage";
  const isEop = meta?.payout_mode === "end_of_period";

  return (
    <div className="w-full">
      <div className="overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs">
            <tr>
              <th className="text-left px-4 py-2.5 w-12">#</th>
              <th className="text-left px-4 py-2.5">Slab</th>
              <th className="text-right px-4 py-2.5">Scan Points From</th>
              <th className="text-right px-4 py-2.5">To</th>
              <th className="text-right px-4 py-2.5">
                {isPercent ? "Reward %" : "Reward Points"}
              </th>
            </tr>
          </thead>
          <tbody>
            {slabs.map((slab, i) => (
              <tr key={`${slab.name}-${i}`} className="border-t border-gray-100">
                <td className="px-4 py-2.5 text-gray-400">{i + 1}</td>
                <td className="px-4 py-2.5 font-medium text-gray-900">
                  {slab.name}
                  {i === slabs.length - 1 && (
                    <span className="ml-2 text-[11px] font-normal text-gray-400">
                      highest
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right text-gray-700">
                  {Number(slab.rangeStart).toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-right text-gray-700">
                  {Number(slab.rangeEnd).toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-right font-semibold text-gray-900">
                  {Number(slab.bonus).toLocaleString()}
                  {isPercent ? "%" : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500 mt-3">
        {isEop
          ? "End of period: only the HIGHEST slab reached is paid, once, after the period closes."
          : "Realtime: every slab crossed pays, as the user crosses it."}
      </p>
    </div>
  );
};

const gradients = [
  "bg-gradient-to-r from-orange-700 to-orange-400",
  "bg-gradient-to-r from-rose-700 to-rose-400",
  "bg-gradient-to-r from-gray-600 to-gray-400",
  "bg-gradient-to-r from-indigo-600 to-indigo-400",
  "bg-gradient-to-r from-blue-600 to-blue-400",
  "bg-gradient-to-r from-blue-600 to-blue-400",
];

const stars = [Gold, platinum, silver, silver, silver, silver];

// Number of periods a scheme window splits into for a given period length.
const countPeriods = (start, end, periodDays) => {
  if (!start || !end || !periodDays) return 0;
  const MS = 86_400_000;
  const s = Date.parse(`${String(start).slice(0, 10)}T00:00:00Z`);
  const e = Date.parse(`${String(end).slice(0, 10)}T00:00:00Z`);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  return Math.ceil((e + MS - s) / (periodDays * MS));
};

const prettyDate = (d) =>
  d
    ? new Date(`${String(d).slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

const MembershipList = () => {
  // Read at RENDER time: vendorData lands in sessionStorage during login,
  // so a module-level const captures the wrong value on first mount.
  const isSchemeMembership = isSchemeMembershipEnabled();
  const addMembershipPermission = hasPermission("addMembership");
  const manageMembershipPermission = hasPermission("manageMembership");
  const deleteMembershipPermission = hasPermission("deleteMembership");

  const columns = [
    {
      name: "SNo",
      selector: (row, idx) => idx + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "User Type",
      selector: (row) => row.user_type,
      sortable: true,
    },
    isSchemeMembership
      ? {
          name: "Progression",
          selector: (row) =>
            row.mode === "periodic"
              ? `Periodic · ${row.period_days}d`
              : "Lifetime",
          cell: (row) => (
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                row.mode === "periodic"
                  ? "bg-violet-50 text-violet-700 border-violet-200"
                  : "bg-blue-50 text-blue-700 border-blue-200"
              }`}
            >
              {row.mode === "periodic"
                ? `Periodic · ${row.period_days}d`
                : "Lifetime"}
            </span>
          ),
          sortable: true,
        }
      : {
          name: "Range Type",
          selector: (row) =>
            row.range_type == 1
              ? " Purchase"
              : row.range_type == 2
              ? "Scan"
              : row.range_type == 3
              ? "QrMrp"
              : "Points",
          sortable: true,
          // width: "120px",
        },
    {
      name: "Start date",
      selector: (row) => new Date(row.start_date).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "End date",
      selector: (row) => new Date(row.end_date).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "Created By",
      selector: (row) => row.created_by_name,
      sortable: true,
    },
    {
      name: "Created At",
      selector: (row) => new Date(row.created_at).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "",
      selector: (row) => (
        <button
          className="flex justify-center items-center rounded bg-green-500 px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#28a745] transition duration-150 ease-in-out hover:bg-green-600 hover:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)] focus:bg-green-600 focus:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)] focus:outline-none focus:ring-0 active:bg-green-700 active:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(40,167,69,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.2),0_4px_18px_0_rgba(40,167,69,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.2),0_4px_18px_0_rgba(40,167,69,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.2),0_4px_18px_0_rgba(40,167,69,0.1)]"
          onClick={() =>
            fetchMembershipById({
              token: sessionStorage.getItem("access_token"),
              tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))
                .tenant_id,
              id: row.id,
            })
          }
        >
          <HiEye className="mr-0.5" />
          View
        </button>
      ),
      sortable: true,
      width: "110px",
    },

    {
      name: "",
      selector: (row) => (
        <button
          className="flex justify-center items-center rounded bg-green-500 px-3 pb-1.5 break-normal pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#28a745] transition duration-150 ease-in-out hover:bg-green-600 hover:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)] focus:bg-green-600 focus:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)] focus:outline-none focus:ring-0 active:bg-green-700 active:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.3),0_4px_18px_0_rgba(40,167,69,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(40,167,69,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.2),0_4px_18px_0_rgba(40,167,69,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.2),0_4px_18px_0_rgba(40,167,69,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(40,167,69,0.2),0_4px_18px_0_rgba(40,167,69,0.1)]"
          onClick={() =>
            navigate("history", {
              state: row,
            })
          }
        >
          History
        </button>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "",
      selector: (row) => (
        // editMembershipPermission &&
        <div>
          <button
            onClick={() =>
              navigate(isSchemeMembership ? "update" : "add", {
                state: row,
              })
            }
            className="flex justify-center items-center rounded bg-gray-500 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-gray-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-gray-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-gray-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
          >
            <AiOutlineEdit className="mr-0.5" />
            Edit
          </button>
        </div>
      ),
      width: "110px",
    },

    {
      name: "",
      selector: (row) => (
        // manageMembershipPermission &&
        <div>
          <button
            onClick={() =>
              navigate("manage", {
                state: row,
              })
            }
            className="flex justify-center items-center rounded bg-primary px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
          >
            <AiOutlineEdit className="mr-0.5" />
            Manage
          </button>
        </div>
      ),
      width: "140px",
    },
    {
      name: "",
      selector: (row) => (
        deleteMembershipPermission && (
        <div>
          <button
            onClick={() => handleDelete(row)}
            className="flex justify-center items-center rounded bg-danger px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#dc4c64] transition duration-150 ease-in-out hover:bg-danger-600 hover:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.3),0_4px_18px_0_rgba(220,76,100,0.2)] focus:bg-danger-600 focus:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.3),0_4px_18px_0_rgba(220,76,100,0.2)] focus:outline-none focus:ring-0 active:bg-danger-700 active:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.3),0_4px_18px_0_rgba(220,76,100,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(220,76,100,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.2),0_4px_18px_0_rgba(220,76,100,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.2),0_4px_18px_0_rgba(220,76,100,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(220,76,100,0.2),0_4px_18px_0_rgba(220,76,100,0.1)]"
          >
            <AiOutlineDelete className="mr-1" />
            Delete
          </button>
        </div>
        )
      ),
      width: "140px",
    },
  ];

  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [schemeSlabs, setSchemeSlabs] = useState([]);

  const [tiers, setTiers] = useState([]);
  const [rangeType, setRangeType] = useState(null);
  const [membershipMeta, setMembershipMeta] = useState(null);

  const [
    fetchAllMembership,
    {
      data: membershipData,
      isLoading: MembershipIsLoading,
      error: MembershipError,
    },
  ] = useFetchAllMembershipMutation();

  const [
    fetchMembershipById,
    { data: fetchMembershipData, error: fetchMembershipError },
  ] = useFetchMembershipByIdMutation();

  useEffect(() => {
    if (fetchMembershipData && fetchMembershipData.body) {
      console.log("fetch membership data", fetchMembershipData);
      setRangeType(
        fetchMembershipData.body[0].range_type == 1
          ? "Purchase"
          : fetchMembershipData.body[0].range_type == 2
          ? "Scan" : fetchMembershipData.body[0].range_type == 3 ? "QrMrp" : "Points",
      );
      const arr = [...fetchMembershipData.body[0].tiers].reverse();
      setTiers(arr);
      const meta = fetchMembershipData.body[0];
      setMembershipMeta({
        mode: meta.mode || "lifetime",
        period_days: meta.period_days,
        payout_mode: meta.payout_mode || "realtime",
        reward_type: meta.reward_type || "fixed",
        activated_at: meta.activated_at || null,
        start_date: meta.start_date,
        end_date: meta.end_date,
      });

      // Order by range_start, the value the engine resolves tiers on — NOT by
      // tier.rank, which is display-only and may use either convention.
      // Rows with tier === null mean no slabs saved yet (LEFT JOIN).
      setSchemeSlabs(
        [...fetchMembershipData.body]
          .filter((r) => r && r.tier)
          .map((r) => ({
            name: r.tier?.name ?? "",
            rangeStart: Number(r.range_start ?? 0),
            rangeEnd: Number(r.range_end ?? 0),
            bonus: Number(r.tier?.bonus ?? 0),
          }))
          .sort((a, b) => a.rangeStart - b.rangeStart),
      );
      setModalData(convertToTierFormat([...fetchMembershipData.body]));
      setShowModal(true);
    }

    if (fetchMembershipError) {
      console.log("fetch membership error", fetchMembershipError);
      toast("Something went wrong , Please refresh the page and try again", {
        id: "errroRefresh",
      });
    }
  }, [fetchMembershipData, fetchMembershipError]);

  useEffect(() => {
    // const hasMembershipView = hasPermission("Membership");

    // if (!hasMembershipView) {
    //   navigate(`/genefied/${getVendorName()}/`);
    // }
    fetchAllMembership({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      limit: LIST_PAGE_SIZE,
      offset: 0,
    });
  }, []);

  console.log(modalData);

  const [
    deleteMembership,
    {
      data: deleteMembershipData,
      isLoading: deleteMembershipIsLoading,
      error: deleteMembershipError,
    },
  ] = useDeleteMembershipByIdMutation();

  const handleDelete = (data) => {
    toast(
      (t) => (
        <span>
          <h6>Are you sure you want to delete this ?</h6>
          <div className="mt-3 d-flex">
            <button
              className="btn btn-outline-danger btn-sm ml-auto mr-1"
              onClick={() => {
                deleteMembership({
                  token: sessionStorage.getItem("access_token"),
                  tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))
                    .tenant_id,
                  id: data.id,
                });
                toast.dismiss(t.id);
              }}
            >
              Yes
            </button>
            <button
              className="btn btn-outline-primary btn-sm mr-auto ml-1"
              onClick={() => {
                toast.dismiss(t.id);
              }}
            >
              No
            </button>
          </div>
        </span>
      ),
      {
        id: "deleteMembership",
      },
    );
    return;
  };

  useEffect(() => {
    if (deleteMembershipData) {
      toast.success("Deleted Successfully.");
      // Refetch only after the delete lands, otherwise the deleted row races
      // back into the list.
      fetchAllMembership({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        limit: LIST_PAGE_SIZE,
        offset: 0,
      });
    }

    if (deleteMembershipError) {
      toast.error("Something went wrong", {
        id: "deleteMembershipError",
      });
    }
  }, [deleteMembershipError, deleteMembershipData]);

  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme
    .color_shades;

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full ">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            {isSchemeMembership ? "Scheme Membership" : "Membership"}
            <LinksComponent />
          </div>

          <div className="p-3">
            <div className="p-3 w-full bg-white shadow rounded-lg">
              <DataTableComponent
              keyField={(row) => row.id}
                columns={columns}
                // title={"Product List"}
                data={membershipData ? membershipData.body.data : []}
                // loading={loading}
                // totalRows={totalRows}
                // handlePerRowsChange={handlePerRowsChange}
                // handlePageChange={handlePageChange}
              ></DataTableComponent>
              {
                  addMembershipPermission &&
                <div
                  className="fixed bottom-0 right-16 w-16 h-16 mr-10 mb-2 cursor-pointer"
                  id="box_btn"
                >
                  <div className="text-right">
                    <Menu className="relative inline-block text-left">
                      <div>
                        <Menu.Button
                          onClick={() => navigate(isSchemeMembership ? `create` : `add`)}
                          className="inline-flex justify-cente w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 "
                          style={{ backgroundColor: `${primaryColor["700"]}` }}
                        >
                          <HiOutlinePlus className="w-full h-full p-3" />
                        </Menu.Button>
                      </div>
                    </Menu>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
        {showModal && modalData && (
          <ModalComponent
            size={"6xl"}
            data={""}
            show={showModal}
            setShow={setShowModal}
          >
            <div className="w-full rounded-sm p-4">
              <div className="rounded-lg w-full overflow-x-scroll flex flex-col justify-start items-start space-y-2">
                <div class="w-full h-full rounded-lg ">
                  <h1 className="text-3xl pb-2 mt-2 text-center font-bold">
                    Membership Details
                  </h1>

                  {/* Scheme membership: progression + period summary */}
                  {isSchemeMembership && membershipMeta && (
                    <div className="flex flex-wrap items-center justify-center gap-2 pb-4">
                      <span
                        className={`text-xs font-semibold px-3 py-1 rounded-full border ${
                          membershipMeta.mode === "periodic"
                            ? "bg-violet-50 text-violet-700 border-violet-200"
                            : "bg-blue-50 text-blue-700 border-blue-200"
                        }`}
                      >
                        {membershipMeta.mode === "periodic"
                          ? `Periodic · ${membershipMeta.period_days}-day periods`
                          : "Lifetime Scan Points"}
                      </span>
                      <span className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                        {prettyDate(membershipMeta.start_date)} –{" "}
                        {prettyDate(membershipMeta.end_date)}
                      </span>
                      {membershipMeta.mode === "periodic" && (
                        <span className="text-xs font-medium px-3 py-1 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
                          {countPeriods(
                            membershipMeta.start_date,
                            membershipMeta.end_date,
                            Number(membershipMeta.period_days),
                          )}{" "}
                          periods
                        </span>
                      )}
                      <span
                        className={`text-xs font-semibold px-3 py-1 rounded-full border ${
                          membershipMeta.payout_mode === "end_of_period"
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200"
                        }`}
                      >
                        {membershipMeta.payout_mode === "end_of_period"
                          ? "Paid at end of period"
                          : "Paid in realtime"}
                      </span>
                      <span className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                        {membershipMeta.reward_type === "percentage"
                          ? "Percentage reward"
                          : "Fixed reward"}
                      </span>
                      {/* A draft accrues nothing — the engine skips it entirely,
                          which otherwise looks identical to "broken". */}
                      <span
                        className={`text-xs font-semibold px-3 py-1 rounded-full border ${
                          membershipMeta.activated_at
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-gray-100 text-gray-500 border-gray-300"
                        }`}
                      >
                        {membershipMeta.activated_at ? "Active" : "Draft — not accruing"}
                      </span>
                    </div>
                  )}
                  {isSchemeMembership ? (
                    <SchemeSlabTable slabs={schemeSlabs} meta={membershipMeta} />
                  ) : (
                  <div class={`grid grid-cols-${tiers.length} gap-4`}>
                    {/* <div className="border border-gray-200 rounded-2xl pb-3">
                      <div className="bg-gradient-to-r from-gray-600 to-gray-400 px-3 pb-20 pt-4 rounded-t-2xl">
                        <h2 className="block text-center text-2xl font-semibold text-white">
                          Silver
                        </h2>
                      </div>

                      <div className="text-center m-auto">
                        <div className="rounded_c rounded-full flex justify-center items-center bg-white  ">
                          <span className="rounded-full p-2">
                            <img
                              src={silver}
                              className="max-h-full max-w-full "
                            />
                          </span>
                        </div>
                      </div>

                      <ul className="px-4">
                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Scan Value
                          </span>
                          <span className="font-medium text-md">21-50</span>
                        </li>

                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Scan/Month
                          </span>
                          <span className="font-medium text-md">21</span>
                        </li>

                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Points Multiplier %
                          </span>
                          <span className="font-medium text-md">2</span>
                        </li>

                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Gift Discount %
                          </span>
                          <span className="font-medium text-md">10</span>
                        </li>

                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Cashback Option
                          </span>
                          <span className="font-medium text-md text-green-700">
                            Yes
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className="border border-gray-200 rounded-2xl pb-3">
                      <div className="bg-gradient-to-r from-amber-700 to-amber-400 px-3 pb-20 pt-4 rounded-t-2xl">
                        <h2 className="block text-center text-2xl font-semibold text-white">
                          Gold
                        </h2>
                      </div>

                      <div className="text-center m-auto">
                        <div className="rounded_c rounded-full flex justify-center items-center bg-white  ">
                          <span className="rounded-full p-2">
                            <img
                              src={Gold}
                              className="max-h-full max-w-full "
                            />
                          </span>
                        </div>
                      </div>

                      <ul className="px-4">
                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Scan Value
                          </span>
                          <span className="font-medium text-md">11-20</span>
                        </li>

                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Scan/Month
                          </span>
                          <span className="font-medium text-md">11</span>
                        </li>

                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Points Multiplier %
                          </span>
                          <span className="font-medium text-md">1.5</span>
                        </li>

                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Gift Discount %
                          </span>
                          <span className="font-medium text-md">5</span>
                        </li>

                        <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                          <span className="font-medium text-md">
                            Cashback Option
                          </span>
                          <span className="font-medium text-md text-green-700">
                            Yes
                          </span>
                        </li>
                      </ul>
                    </div> */}

                    {tiers.map((tier, idx) => {
                      const gradientIdx = Math.min(
                        tier.id - 1,
                        gradients.length - 1,
                      );
                      const starIdx = Math.min(tier.id - 1, stars.length - 1);
                      return (
                        <div
                          key={idx}
                          className="border border-gray-200 rounded-2xl pb-3"
                        >
                          <div
                            className={
                              " px-3 pb-20 pt-4 rounded-t-2xl  " +
                              gradients[gradientIdx]
                            }
                          >
                            <h2 className="block text-center text-2xl font-semibold text-white">
                              {tier.name}
                            </h2>
                          </div>
                          <div className="text-center m-auto">
                            <div className="rounded_c rounded-full flex justify-center items-center bg-white">
                              <span className="rounded-full p-2">
                                <img
                                  src={stars[starIdx]}
                                  className="max-h-full max-w-full"
                                />
                              </span>
                            </div>
                          </div>

                          <ul className="px-4">
                            <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                              <span className="font-medium text-md">
                                {isSchemeMembership
                                  ? "Scan Points Slab"
                                  : `${rangeType} Slab`}
                              </span>
                              <span className="font-medium text-md">
                                {Number(
                                  modalData[tier.name].rangeStart,
                                ).toLocaleString()}{" "}
                                -{" "}
                                {Number(
                                  modalData[tier.name].rangeEnd,
                                ).toLocaleString()}
                              </span>
                            </li>

                            {isSchemeMembership ? (
                              <>
                                <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                                  <span className="font-medium text-md">
                                    Bonus (pts)
                                  </span>
                                  <span className="font-medium text-md">
                                    {modalData[tier.name].bonus}
                                  </span>
                                </li>
                              </>
                            ) : (
                              <>
                                <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                                  <span className="font-medium text-md">
                                    {rangeType}/Month
                                  </span>
                                  <span className="font-medium text-md">
                                    {Number(
                                      modalData[tier.name].perMonth,
                                    ).toLocaleString()}
                                  </span>
                                </li>

                                <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                                  <span className="font-medium text-md">
                                    Points Multiplier %
                                  </span>
                                  <span className="font-medium text-md">
                                    {modalData[tier.name].pointsMultiplier}
                                  </span>
                                </li>

                                <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                                  <span className="font-medium text-md">
                                    Gift Discount %
                                  </span>
                                  <span className="font-medium text-md">
                                    {modalData[tier.name].giftDiscount}
                                  </span>
                                </li>

                                <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                                  <span className="font-medium text-md">
                                    Cashback Option
                                  </span>
                                  {modalData[tier.name].cashback ? (
                                    <span className="font-medium text-md text-green-700">
                                      Yes
                                    </span>
                                  ) : (
                                    <span className="font-medium text-md text-red-700">
                                      No
                                    </span>
                                  )}
                                </li>

                                <li className="flex border-b border-gray-100 justify-between pb-2.5 mb-2.5">
                                  <span className="font-medium text-md">
                                    Cash Benefit
                                  </span>
                                  <span className="font-medium text-md">
                                    {modalData[tier.name]?.cash_benefit}
                                  </span>
                                </li>
                              </>
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                  )}


                </div>
              </div>
            </div>
          </ModalComponent>
        )}
      </div>
    </>
  );
};

export default MembershipList;
